import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    CircularProgress,
    Chip,
    Alert,
    Grid,
    Stack
} from '@mui/material';
import {
    Quiz as QuizIcon,
    AutoMode as AIIcon,
    Lock as LockIcon,
    Timer,
    Assignment,
    CheckCircle,
    History
} from '@mui/icons-material';
import { getNextPracticeQuiz, StaticQuizDTO, PrerequisiteSuggestionDTO } from '../../services/quizService';
import QuizRunner from './QuizRunner';
import PastAttemptsDialog from './PastAttemptsDialog';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface PracticeQuizCardProps {
    topicId: number;
    subjectId: number; // For navigation context if needed
}

const PracticeQuizCard: React.FC<PracticeQuizCardProps> = ({ topicId }) => {
    const [loading, setLoading] = useState(false);
    const [checkingQuiz, setCheckingQuiz] = useState(true);
    const [availableQuizzes, setAvailableQuizzes] = useState<StaticQuizDTO[]>([]);
    const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
    const [activeQuiz, setActiveQuiz] = useState<StaticQuizDTO | null>(null);
    const [blockedReason, setBlockedReason] = useState<string | null>(null);
    const [showPastAttempts, setShowPastAttempts] = useState(false);

    // Check for available quiz on mount
    useEffect(() => {
        checkAvailableQuiz();
    }, [topicId]);

    const checkAvailableQuiz = async () => {
        setCheckingQuiz(true);
        try {
            const result = await getNextPracticeQuiz(topicId);
            if (result && !('suggestedTopicIds' in result)) {
                setAvailableQuizzes([result as StaticQuizDTO]);
            }
        } catch (error) {
            console.error("Failed to check for quiz:", error);
        } finally {
            setCheckingQuiz(false);
        }
    };

    const handleStartPractice = async () => {
        if (availableQuizzes.length > 0) {
            setActiveQuiz(availableQuizzes[currentQuizIndex]);
            return;
        }

        setLoading(true);
        setBlockedReason(null);
        try {
            const result = await getNextPracticeQuiz(topicId);

            if (!result) {
                setBlockedReason("No practice quiz available at this time. Please check back later.");
            } else if ('suggestedTopicIds' in result) {
                // It's a suggestion DTO
                setBlockedReason(`Practice Blocked: ${result.reason}. Please review prerequisite topics.`);
            } else {
                // It's a quiz
                const quiz = result as StaticQuizDTO;
                // Ensure topicId is set
                if (!quiz.topicId) {
                    quiz.topicId = topicId;
                }
                setAvailableQuizzes([quiz]);
                setCurrentQuizIndex(0);
                setActiveQuiz(quiz);
            }
        } catch (error: any) {
            console.error("Failed to start practice:", error);
            const errorMsg = error?.response?.data?.message || error?.message || "Failed to load practice quiz";
            setBlockedReason(`Error: ${errorMsg}. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    const handleTryAnother = async () => {
        setLoading(true);
        try {
            const result = await getNextPracticeQuiz(topicId);
            if (result && !('suggestedTopicIds' in result)) {
                const newQuiz = result as StaticQuizDTO;
                // Check if this quiz is already in the list
                const existingIndex = availableQuizzes.findIndex(q => q.id === newQuiz.id);
                // Ensure topicId is set
                if (!newQuiz.topicId) {
                    newQuiz.topicId = topicId;
                }
                if (existingIndex === -1) {
                    setAvailableQuizzes([...availableQuizzes, newQuiz]);
                    setCurrentQuizIndex(availableQuizzes.length);
                    setActiveQuiz(newQuiz);
                } else {
                    setCurrentQuizIndex(existingIndex);
                    setActiveQuiz(availableQuizzes[existingIndex]);
                }
            } else {
                setBlockedReason("No additional quizzes available at this time.");
            }
        } catch (error) {
            console.error("Failed to load another quiz:", error);
            setBlockedReason("Failed to load another quiz. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleQuizComplete = (attempt: any) => {
        // QuizRunner now handles showing results screen internally
        // We don't close the dialog here - let QuizRunner show results
        // The dialog will only close when user explicitly clicks close/exit from results screen
        console.log('Quiz completed:', attempt);
        // Don't close dialog - QuizRunner will show results screen
    };

    const handleCloseQuiz = () => {
        // Allow closing without confirmation since results are saved
        setActiveQuiz(null);
    };

    return (
        <>
            <Card 
                elevation={2}
                sx={{ 
                    mb: 3, 
                    borderLeft: '6px solid', 
                    borderColor: availableQuizzes.length > 0 ? 'success.main' : 'warning.main',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)'
                    }
                }}
            >
                <CardContent>
                    <Grid container spacing={3} alignItems="center">
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                                <Box 
                                    sx={{ 
                                        p: 1, 
                                        borderRadius: '50%', 
                                        bgcolor: availableQuizzes.length > 0 ? 'success.light' : 'warning.light',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <QuizIcon color={availableQuizzes.length > 0 ? 'success' : 'warning'} />
                                </Box>
                                <Typography variant="h6" fontWeight={600}>
                                    Practice Quiz
                                </Typography>
                                {checkingQuiz && (
                                    <CircularProgress size={16} sx={{ ml: 1 }} />
                                )}
                            </Box>
                            
                            {availableQuizzes.length > 0 ? (
                                <Stack spacing={1.5} sx={{ mt: 2 }}>
                                    <Typography variant="body1" fontWeight={500}>
                                        {availableQuizzes[currentQuizIndex].title}
                                    </Typography>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        {availableQuizzes[currentQuizIndex].questions && availableQuizzes[currentQuizIndex].questions.length > 0 && (
                                            <Chip 
                                                icon={<Assignment />}
                                                label={`${availableQuizzes[currentQuizIndex].questions.length} Questions`} 
                                                size="small" 
                                                variant="outlined"
                                            />
                                        )}
                                        {availableQuizzes[currentQuizIndex].questions && availableQuizzes[currentQuizIndex].questions.length > 0 && 
                                         availableQuizzes[currentQuizIndex].questions[0]?.type && (
                                            <Chip 
                                                label={`Type: ${availableQuizzes[currentQuizIndex].questions[0].type.replace('_', ' ')}`}
                                                size="small" 
                                                variant="outlined"
                                                color="primary"
                                            />
                                        )}
                                        {availableQuizzes[currentQuizIndex].timeLimitMinutes && (
                                            <Chip 
                                                icon={<Timer />}
                                                label={`${availableQuizzes[currentQuizIndex].timeLimitMinutes} min`} 
                                                size="small" 
                                                variant="outlined"
                                            />
                                        )}
                                        {availableQuizzes[currentQuizIndex].minPassPercent && (
                                            <Chip 
                                                icon={<CheckCircle />}
                                                label={`${availableQuizzes[currentQuizIndex].minPassPercent}% to pass`} 
                                                size="small" 
                                                variant="outlined"
                                                color="success"
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Test your understanding with this practice quiz. You can retake it as many times as you need.
                                    </Typography>
                                </Stack>
                            ) : !checkingQuiz && !blockedReason ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Test your understanding with practice questions.
                                </Typography>
                            ) : null}
                            
                            {blockedReason && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    {blockedReason}
                                </Alert>
                            )}
                        </Grid>
                        
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display="flex" gap={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={() => setShowPastAttempts(true)}
                                    startIcon={<History />}
                                    sx={{ minWidth: 140 }}
                                >
                                    Past Attempts
                                </Button>
                                <Button
                                    variant="contained"
                                    color={availableQuizzes.length > 0 ? 'primary' : 'secondary'}
                                    size="large"
                                    fullWidth={false}
                                    onClick={handleStartPractice}
                                    disabled={loading || checkingQuiz}
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <QuizIcon />}
                                    sx={{ 
                                        minWidth: 160,
                                        py: 1.5,
                                        px: 3,
                                        fontWeight: 600
                                    }}
                                >
                                    {loading ? 'Loading...' : checkingQuiz ? 'Checking...' : availableQuizzes.length > 0 ? 'Start Quiz' : 'Check Availability'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Dialog
                open={!!activeQuiz}
                fullScreen
                onClose={handleCloseQuiz}
            >
                <Box sx={{ position: 'relative', height: '100%', bgcolor: '#f5f5f5' }}>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleCloseQuiz}
                        aria-label="close"
                        sx={{ position: 'absolute', right: 16, top: 16, zIndex: 1200 }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {activeQuiz && (
                        <QuizRunner
                            quiz={activeQuiz}
                            mode="PRACTICE"
                            onComplete={handleQuizComplete}
                            onExit={() => setActiveQuiz(null)}
                            onTryAnother={handleTryAnother}
                            hasMoreQuizzes={true} // Always allow trying another quiz
                        />
                    )}
                </Box>
            </Dialog>

            <PastAttemptsDialog
                open={showPastAttempts}
                onClose={() => setShowPastAttempts(false)}
                topicId={topicId}
            />
        </>
    );
};

export default PracticeQuizCard;
